import { Stack } from '@mui/material';
import { ConversationStatus } from '@provision/conversation-types';
import { useCallback, useMemo } from 'react';
import { CopyToClipboardButton } from 'src/components';
import { useCardCustomAttributeRows } from 'src/components/CustomAttributes/use-card-custom-attribute-rows';
import { useLoadCardCustomAttributeValues } from 'src/components/CustomAttributes/use-load-card-custom-attribute-values';
import { SidePanelCaption } from 'src/components/SidePanelCaption';
import { sidePanelActionButtonSx } from 'src/components/SidePanelRow';
import { SiteRelatedTabs } from 'src/components/SidePanelTabs/SiteTab/site-related-tabs';
import { SiteTab } from 'src/components/SidePanelTabs/SiteTab/site-tab';
import { SiteMenu } from 'src/components/SiteComponents/site-menu';
import { SiteShareButton } from 'src/components/SiteComponents/site-share-button';
import { Address, Conversation } from 'src/graphql';
import { useUpdateAssociatedSite } from 'src/pages/Sites/use-update-associated-site';
import useConversationViewsStore from 'src/store/conversation-views';
import { AssociatedSiteFormValues, useAssociatedSiteForm } from './use-associated-site-form';

export const ConversationSite = ({
  conversation,
  address,
  variant,
}: {
  conversation?: Conversation;
  address?: Partial<Address>;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
}) => {
  const { associatedAddressesPlaceholders } = useConversationViewsStore();

  const currentAddress = useMemo(
    () =>
      (address ??
        conversation?.address ??
        associatedAddressesPlaceholders[conversation?.conversationId!]?.[0]) as Address,
    [
      address,
      associatedAddressesPlaceholders,
      conversation?.address,
      conversation?.conversationId,
    ],
  );

  const disabled =
    (conversation?.conversationStatusId as ConversationStatus) === ConversationStatus.Closed ||
    !!conversation?.isDeleted;

  const updateAssociatedSite = useUpdateAssociatedSite(
    currentAddress,
    conversation?.conversationId,
  );

  useLoadCardCustomAttributeValues({
    sectionKey: 'site',
    entity: 'Address',
    entityIds: [currentAddress.addressId],
  });

  const customAttributeRows = useCardCustomAttributeRows({
    sectionKey: 'site',
    entity: 'Address',
    entityId: currentAddress.addressId,
    partnerId: currentAddress.partner?.partnerId ?? currentAddress.partnerId,
    disabled: disabled || !!currentAddress.isDeleted,
  });

  const { associatedSiteFormFields } = useAssociatedSiteForm(currentAddress, disabled, {
    customAttributeRows,
  });

  const onSubmitAssociatedSite = useCallback(
    (values: AssociatedSiteFormValues) => {
      void updateAssociatedSite(values);
    },
    [updateAssociatedSite],
  );

  return (
    <SiteTab
      address={currentAddress}
      onSubmit={onSubmitAssociatedSite}
      fields={associatedSiteFormFields}
      hideAddressRow
      propertiesHeader={
        <>
          <Stack
            data-testid="conversation-site-actions"
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="flex-end"
            paddingBottom={0.75}
          >
            <CopyToClipboardButton
              content={currentAddress?.fullAddress!}
              sx={sidePanelActionButtonSx}
            />
            <SiteShareButton
              address={currentAddress}
              partnerId={currentAddress?.partner?.partnerId ?? currentAddress?.partnerId}
              sx={sidePanelActionButtonSx}
            />
            <SiteMenu address={currentAddress} conversation={conversation} />
          </Stack>
          <SidePanelCaption
            sx={{
              marginTop: 0.25,
              padding: (theme) => theme.spacing(0.75, 0, 1),
            }}
          >
            Properties
          </SidePanelCaption>
        </>
      }
    >
      <SiteRelatedTabs address={currentAddress} variant={variant} disabled={disabled} />
    </SiteTab>
  );
};
